import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { 
  Building2, 
  Save, 
  Loader2,
  Upload,
  Image,
  Landmark,
  Phone,
  Globe,
  MapPin,
  FileText,
  Check,
  X,
  Trash2,
  Mail
} from "lucide-react";
import { toast } from "sonner";
import { useBusiness } from "@/contexts/BusinessContext";
import DbMigrationCard from "./DbMigrationCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyConfigTab() {
  const { businessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [config, setConfig] = useState({
    company_name: "",
    rfc: "",
    logo_url: "",
    logo_email_url: "",
    // Datos bancarios
    bank_name: "",
    bank_clabe: "",
    bank_account: "",
    // Contacto
    phone: "",
    whatsapp: "",
    website: "",
    email: "",
    // Dirección
    address_line1: "",
    address_line2: "",
    address_neighborhood: "",
    address_city: "",
    address_state: "",
    // Términos por defecto
    default_terms: "",
    default_terms_rental: ""
  });
  
  const [originalConfig, setOriginalConfig] = useState(null);

  useEffect(() => {
    if (businessId) {
      fetchConfig();
    }
  }, [businessId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/company-config/${businessId}`);
      if (response.data.success) {
        const data = response.data.config;
        setConfig({
          company_name: data.company_name || "",
          rfc: data.rfc || "",
          logo_url: data.logo_url || "",
          logo_email_url: data.logo_email_url || "",
          bank_name: data.bank_name || "",
          bank_clabe: data.bank_clabe || "",
          bank_account: data.bank_account || "",
          phone: data.phone || "",
          whatsapp: data.whatsapp || "",
          website: data.website || "",
          email: data.email || "",
          address_line1: data.address_line1 || "",
          address_line2: data.address_line2 || "",
          address_neighborhood: data.address_neighborhood || "",
          address_city: data.address_city || "",
          address_state: data.address_state || "",
          default_terms: data.default_terms || "",
          default_terms_rental: data.default_terms_rental || ""
        });
        setOriginalConfig(data);
      }
    } catch (error) {
      console.error("Error fetching company config:", error);
      toast.error("Error al cargar la configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/company-config/${businessId}`, config);
      if (response.data.success) {
        toast.success("Configuración guardada correctamente");
        setOriginalConfig(response.data.config);
      }
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error("Por favor selecciona una imagen válida");
      return;
    }

    // Validar tamaño (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen debe ser menor a 2MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('business_id', businessId);

      const response = await axios.post(`${API}/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setConfig(prev => ({ ...prev, logo_url: response.data.url }));
        toast.success("Logo subido correctamente");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Error al subir el logo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setConfig(prev => ({ ...prev, logo_url: "" }));
  };

  const handleLogoEmailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error("Selecciona una imagen válida"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Máximo 2MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('business_id', businessId);
      const response = await axios.post(`${API}/upload-logo-email`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        setConfig(prev => ({ ...prev, logo_email_url: response.data.url }));
        toast.success("Logo de correo subido");
      }
    } catch (error) {
      toast.error("Error al subir el logo de correo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogoEmail = () => {
    setConfig(prev => ({ ...prev, logo_email_url: "" }));
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-config-tab">
      <DbMigrationCard />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-blue-500" />
          <h2 className="text-xl font-semibold text-white">Configuración de Empresa</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            hasChanges 
              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
              : 'bg-slate-700 text-slate-400 cursor-not-allowed'
          }`}
          data-testid="save-config-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Guardar Cambios
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo y Datos Básicos */}
        <div className="bg-slate-800 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-medium text-white flex items-center gap-2">
            <Image className="w-5 h-5 text-orange-400" />
            Logo y Datos Básicos
          </h3>
          
          {/* Logo Upload */}
          <div className="space-y-3">
            <label className="block text-sm text-slate-400">Logo de la Empresa</label>
            <div className="flex items-start gap-4">
              <div className="w-32 h-32 bg-slate-900 rounded-lg border-2 border-dashed border-slate-600 flex items-center justify-center overflow-hidden">
                {config.logo_url ? (
                  <img 
                    src={config.logo_url} 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center">
                    <Image className="w-8 h-8 text-slate-500 mx-auto mb-1" />
                    <span className="text-xs text-slate-500">Sin logo</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
                  data-testid="upload-logo-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Subir Logo
                </button>
                {config.logo_url && (
                  <button
                    onClick={handleRemoveLogo}
                    className="flex items-center gap-2 px-3 py-2 text-red-400 hover:text-red-300 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Quitar
                  </button>
                )}
                <p className="text-xs text-slate-500">PNG, JPG. Max 2MB. Para cotizaciones y OC.</p>
              </div>
            </div>
          </div>

          {/* Logo Email */}
          <div className="space-y-3">
            <label className="block text-sm text-slate-400">Logo para Correos Electrónicos</label>
            <p className="text-xs text-slate-500 -mt-2">Versión del logo para fondo claro (se usa en la firma de correos)</p>
            <div className="flex items-start gap-4">
              <div className="w-32 h-20 bg-white rounded-lg border-2 border-dashed border-slate-600 flex items-center justify-center overflow-hidden">
                {config.logo_email_url ? (
                  <img src={config.logo_email_url} alt="Logo Email" className="w-full h-full object-contain p-1" />
                ) : (
                  <div className="text-center">
                    <Mail className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                    <span className="text-[10px] text-slate-400">Sin logo</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input type="file" accept="image/*" onChange={handleLogoEmailUpload} className="hidden" id="logo-email-input" />
                <button onClick={() => document.getElementById('logo-email-input')?.click()} disabled={uploading}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
                  data-testid="upload-logo-email-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Subir Logo Correo
                </button>
                {config.logo_email_url && (
                  <button onClick={handleRemoveLogoEmail} className="flex items-center gap-2 px-3 py-2 text-red-400 hover:text-red-300 text-sm">
                    <Trash2 className="w-4 h-4" /> Quitar
                  </button>
                )}
                <p className="text-xs text-slate-500">PNG con fondo transparente o claro</p>
              </div>
            </div>
          </div>

          {/* Nombre y RFC */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Nombre de la Empresa</label>
              <input
                type="text"
                value={config.company_name}
                onChange={(e) => setConfig({...config, company_name: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="HEBEH EBENEZER S.A. DE C.V."
                data-testid="company-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">RFC</label>
              <input
                type="text"
                value={config.rfc}
                onChange={(e) => setConfig({...config, rfc: e.target.value.toUpperCase()})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none uppercase"
                placeholder="HEB241025A52"
                data-testid="rfc-input"
              />
            </div>
          </div>
        </div>

        {/* Datos Bancarios */}
        <div className="bg-slate-800 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-medium text-white flex items-center gap-2">
            <Landmark className="w-5 h-5 text-green-400" />
            Datos Bancarios
          </h3>
          <p className="text-sm text-slate-400">Estos datos aparecen en el footer de las cotizaciones PDF</p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Banco</label>
              <input
                type="text"
                value={config.bank_name}
                onChange={(e) => setConfig({...config, bank_name: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="Banco Santander"
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">CLABE Interbancaria</label>
              <input
                type="text"
                value={config.bank_clabe}
                onChange={(e) => setConfig({...config, bank_clabe: e.target.value.replace(/\D/g, '')})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none font-mono"
                placeholder="014180655108618493"
                maxLength={18}
                data-testid="bank-clabe-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Número de Cuenta</label>
              <input
                type="text"
                value={config.bank_account}
                onChange={(e) => setConfig({...config, bank_account: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none font-mono"
                placeholder="65-51086184-9"
                data-testid="bank-account-input"
              />
            </div>
          </div>
        </div>

        {/* Datos de Contacto */}
        <div className="bg-slate-800 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-medium text-white flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-400" />
            Datos de Contacto
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Teléfono</label>
              <input
                type="text"
                value={config.phone}
                onChange={(e) => setConfig({...config, phone: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="55 1318 1745"
                data-testid="phone-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">WhatsApp</label>
              <input
                type="text"
                value={config.whatsapp}
                onChange={(e) => setConfig({...config, whatsapp: e.target.value.replace(/\D/g, '')})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="5513181745"
                data-testid="whatsapp-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Sitio Web</label>
              <input
                type="text"
                value={config.website}
                onChange={(e) => setConfig({...config, website: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="www.hebeh.com.mx"
                data-testid="website-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Email</label>
              <input
                type="email"
                value={config.email}
                onChange={(e) => setConfig({...config, email: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="contacto@hebeh.com.mx"
                data-testid="email-input"
              />
            </div>
          </div>
        </div>

        {/* Dirección */}
        <div className="bg-slate-800 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-medium text-white flex items-center gap-2">
            <MapPin className="w-5 h-5 text-red-400" />
            Dirección
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Calle y Número</label>
              <input
                type="text"
                value={config.address_line1}
                onChange={(e) => setConfig({...config, address_line1: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="Av. Jaime Balmes 11"
                data-testid="address-line1-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Interior / Edificio / Piso</label>
              <input
                type="text"
                value={config.address_line2}
                onChange={(e) => setConfig({...config, address_line2: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="Torre B 402 – Piso 4"
                data-testid="address-line2-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Colonia</label>
                <input
                  type="text"
                  value={config.address_neighborhood}
                  onChange={(e) => setConfig({...config, address_neighborhood: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  placeholder="Col. Polanco I Sección"
                  data-testid="address-neighborhood-input"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Alcaldía/Municipio, C.P.</label>
                <input
                  type="text"
                  value={config.address_city}
                  onChange={(e) => setConfig({...config, address_city: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  placeholder="Miguel Hidalgo, C.P. 11510"
                  data-testid="address-city-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Estado</label>
              <input
                type="text"
                value={config.address_state}
                onChange={(e) => setConfig({...config, address_state: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="Ciudad de México"
                data-testid="address-state-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Términos y Condiciones por Defecto */}
      <div className="bg-slate-800 rounded-xl p-6 space-y-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-400" />
          Términos y Condiciones por Defecto
        </h3>
        <p className="text-sm text-slate-400">
          Estos términos se usarán por defecto en las nuevas cotizaciones. Puedes editarlos individualmente en cada cotización.
        </p>
        <textarea
          value={config.default_terms}
          onChange={(e) => setConfig({...config, default_terms: e.target.value})}
          rows={6}
          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none resize-none"
          placeholder="CONDICIONES:&#10;• Precios en Moneda Nacional (MXN)&#10;• Precios incluyen IVA&#10;• Vigencia de la cotización: según fecha indicada&#10;• Entrega sujeta a disponibilidad de inventario&#10;• Forma de pago: A convenir"
          data-testid="default-terms-input"
        />
      </div>

      {/* Términos por Defecto — RENTA */}
      <div className="bg-slate-800 rounded-xl p-6 space-y-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-orange-400" />
          Términos por Defecto — Renta
        </h3>
        <p className="text-sm text-slate-400">
          Estos términos se usarán por defecto en las cotizaciones de <strong>RENTA</strong>. Puedes editarlos individualmente en cada cotización.
        </p>
        <textarea
          value={config.default_terms_rental}
          onChange={(e) => setConfig({...config, default_terms_rental: e.target.value})}
          rows={10}
          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-orange-500 focus:outline-none resize-none font-mono text-sm"
          placeholder="CONDICIONES DE ARRENDAMIENTO:&#10;1. DEPÓSITO: 1 mes de renta como garantía...&#10;2. MANTENIMIENTOS MENORES: Por cuenta del arrendatario...&#10;..."
          data-testid="default-terms-rental-input"
        />
      </div>

      {/* Preview del Footer */}
      <div className="bg-slate-800 rounded-xl p-6 space-y-4">
        <h3 className="text-lg font-medium text-white">Vista Previa del Footer (PDF)</h3>
        <div className="bg-[#0B1623] rounded-lg p-4 flex justify-between text-white text-xs">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Landmark className="w-3 h-3" />
              <span className="font-semibold">{config.bank_name || "Banco Santander"}</span>
            </div>
            <div className="pl-5 opacity-90">
              CLABE: {config.bank_clabe || "014180655108618493"}<br/>
              Cuenta: {config.bank_account || "65-51086184-9"}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-3 h-3" />
              <span className="font-semibold">{config.phone || "55 1318 1745"}</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Globe className="w-3 h-3" />
              <span className="font-semibold">{config.website || "www.hebeh.com.mx"}</span>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-3 h-3" />
              <span className="font-semibold">Dirección</span>
            </div>
            <div className="pl-5 opacity-90">
              {config.address_line1 || "Av. Jaime Balmes 11"}<br/>
              {config.address_line2 || "Torre B 402 – Piso 4"}<br/>
              {config.address_neighborhood || "Col. Polanco I Sección"}<br/>
              {config.address_city || "Miguel Hidalgo, C.P. 11510"}<br/>
              {config.address_state || "Ciudad de México"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
